import { useState, useRef, useEffect } from 'react';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import NewInquiryDialog from './NewInquiryDialog';
import { usePostAuthApi } from '../../hooks/UseApiCalls';
import useFundContext from '../../contexts/Fund/useFundContext';
import { useQueryClient } from '@tanstack/react-query';
import InquiryAnchoredPopover from './InquiryAnchoredPopover';
import { CreateInquiryDtoOut } from '../../models/out/CreateInquiryDtoOut';
import CheckIcon from '@mui/icons-material/Check';
import { InquiryResourceField } from '../../models/common/InquiryResourceField';
import useInquiriesFeature from '../../hooks/useInquiriesFeature';
import styles from './OpenInquiryButton.module.scss';

/**
 * "פתח פנייה" on a row, governed by the 'inquiries' feature flag alone.
 *
 * This button used to read VITE_APP_ENABLED_ROUTES itself, because the screens were gated by
 * that list and the button was not: a coordinator could open an inquiry at a site where
 * 'support/inquiries' was off, and then had nowhere to read the answer.
 *
 * ⚠️ That check is gone on purpose, and removing it is the whole point. The flag now gates the
 * screens too, so keeping both gates meant the two disagreed at exactly the wrong moment:
 * turning the flag ON from /management/feature-flags gave a coordinator the correspondence
 * centre and the sidebar item, but NOT this button - the one place an inquiry is actually
 * opened from. Half a feature, and only a deploy could complete it, which is what the flag
 * exists to avoid.
 *
 * One gate, one source of truth: whatever the switch says, every part of inquiries agrees.
 */

interface OpenInquiryButtonProps {
  resourceType: string;
  resourceId: string;
  /**
   * The row this was opened from, as label/value lines. Shown in the dialog and stored on the
   * inquiry so the office sees what was being asked about without leaving the thread.
   *
   * Optional: a board that has not been given its fields yet keeps working exactly as before,
   * with the resource link alone.
   */
  resourceSummary?: InquiryResourceField[];
  contactId?: number;
  size?: 'small' | 'medium' | 'large';
}

export default function OpenInquiryButton({
  resourceType, resourceId, resourceSummary, contactId, size = 'small',
}: OpenInquiryButtonProps) {
  const { t } = useTranslation();
  const isFeatureOn = useInquiriesFeature();
  const { currentFund } = useFundContext();
  const fundId = currentFund?.id;
  const navigate = useNavigate();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [successInquiryId, setSuccessInquiryId] = useState<number | null>(null);
  const [successAnchorEl, setSuccessAnchorEl] = useState<HTMLElement | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const contactParam = contactId ? `&contactId=${contactId}` : '';
  const createUrl = `funds/${fundId}/inquiries?_=1${contactParam}`;
  const createMutation = usePostAuthApi<{ id: number }, CreateInquiryDtoOut>(createUrl, {
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [`funds/${fundId}/inquiries`] });
      setOpen(false);
      setSuccessAnchorEl(triggerRef.current);
      setSuccessInquiryId(data?.id ?? null);
    },
    onError: () => {
      setErrorMsg(t('inquiries.newInquiryDialog.error'));
    },
  });

  useEffect(() => {
    if (successInquiryId === null) setSuccessAnchorEl(null);
  }, [successInquiryId]);

  const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation();
  const iconSize = size === 'small' ? 18 : size === 'medium' ? 22 : 28;

  // Below every hook, so the hook order stays identical whether or not it renders.
  if (!isFeatureOn) return null;

  return (
    <div onClick={stopPropagation} onMouseDown={stopPropagation}>
      <button
        ref={triggerRef}
        type="button"
        title={t('inquiries.openInquiry')}
        onClick={() => { setOpen(true); setErrorMsg(null); }}
        className="flex items-center justify-center p-1.5 rounded-full hover:bg-(--surface-hover) transition-colors"
        style={{ color: 'var(--font-primary)' }}
      >
        <ChatBubbleOutlineIcon sx={{ fontSize: iconSize }} />
      </button>

      <NewInquiryDialog
        open={open}
        onClose={() => { setOpen(false); setErrorMsg(null); }}
        onSubmit={(dto) => { setErrorMsg(null); createMutation.mutate(dto); }}
        resourceType={resourceType}
        resourceId={resourceId}
        resourceSummary={resourceSummary}
        isSubmitting={createMutation.isPending}
        errorMessage={errorMsg}
      />

      <InquiryAnchoredPopover
        open={successInquiryId !== null}
        anchorEl={successAnchorEl}
        onClose={() => setSuccessInquiryId(null)}
        side="bottom"
        align="center"
      >
        <div className={styles.card}>
          <div className={styles.head}>
            <span className={styles.checkCircle}>
              <CheckIcon sx={{ fontSize: 20 }} />
            </span>
            <span className={styles.headText}>
              <span className={`body-sm-semibold ${styles.title}`}>
                {t('inquiries.newInquiryDialog.success')}
              </span>
              <span className={`body-xs-sm-regular ${styles.subtitle}`}>
                #{successInquiryId}
              </span>
            </span>
          </div>

          <span className={`body-xs-sm-regular ${styles.note}`}>
            {t('inquiries.newInquiryDialog.successNote')}
          </span>

          <button
            type="button"
            className={`body-sm-semibold ${styles.viewBtn}`}
            onClick={() => {
              navigate(`/${fundId}/support/inquiries/${successInquiryId}`);
              setSuccessInquiryId(null);
            }}
          >
            {t('inquiries.newInquiryDialog.viewInquiry')}
          </button>
        </div>
      </InquiryAnchoredPopover>
    </div>
  );
}
